import * as THREE from "three";
import { Line, Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { buildCircleLine, buildDashedSegments } from "./internals/geometry";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

export type GridKind = "rectangular" | "radial" | "irregular";
export type GridLabelMode = "alpha" | "numeric";

export type GridAxisCurve =
  | { curve: "line"; start: [number, number]; end: [number, number] }
  | { curve: "arc"; center: [number, number]; radius: number; startAngle: number; endAngle: number };

export interface GridAxis {
  tag: string;
  curveSpec: GridAxisCurve;
  /** Which endpoint(s) carry a bubble. */
  head?: { start?: boolean; end?: boolean };
}

export interface GridHead {
  /** Diameter of the bubble on the XZ plane in world units. */
  diameter: number;
  /** Text size for the bubble label. */
  fontSize: number;
}

export interface GridOptions extends DatumOptionsBase {
  datumType: DatumType.GRID;
  gridKind: GridKind;
  uAxes: GridAxis[];
  vAxes: GridAxis[];
  head: GridHead;
  /** Characters omitted from auto-generated alpha labels. */
  labelSkip: string[];
  /** Elevation (world Y) at which the grid is drawn. */
  elevation: number;
  /** Render axis lines as dashed. Default `false` (solid). */
  dashedAxes?: boolean;
}

/**
 * Structural grid — a pair of axis families (U, V) labeled per the
 * conventions in ISO 5457 and the US National CAD Standard: letters on
 * one axis, numbers on the other, with I and O skipped to avoid
 * confusion with 1 and 0.
 *
 * Maps to IfcGrid (with IfcGridAxis children and IfcAnnotation bubbles)
 * on IFC export.
 */
export class Grid extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_GRID;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: GridOptions = {
    labelName: "Structural Grid",
    datumType: DatumType.GRID,
    color: DATUM_COLORS.GRID,
    lineWidth: 1,
    visibility: { plan: true, model: true },
    gridKind: "rectangular",
    uAxes: [],
    vAxes: [],
    head: { diameter: 0.7, fontSize: 0.3 },
    labelSkip: ["I", "O"],
    elevation: 0,
    dashedAxes: false,
  };

  constructor(config?: Partial<GridOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
        head: { ...this.propertySet.head, ...(config.head ?? {}) },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  /**
   * Build a rectangular grid from spacing arrays and return the resulting
   * options so callers can pass them straight into the constructor.
   */
  static rectangular(opts: {
    uSpacings: number[];
    vSpacings: number[];
    origin?: [number, number];
    elevation?: number;
    labelName?: string;
    head?: Partial<GridHead>;
  }): Partial<GridOptions> {
    const origin = opts.origin ?? [0, 0];
    const uAxes = Grid.buildParallelAxes(opts.uSpacings, "numeric", "vertical", origin);
    const vAxes = Grid.buildParallelAxes(opts.vSpacings, "alpha", "horizontal", origin);
    return {
      gridKind: "rectangular",
      uAxes,
      vAxes,
      elevation: opts.elevation ?? 0,
      labelName: opts.labelName ?? "Structural Grid",
      head: { diameter: 0.7, fontSize: 0.3, ...(opts.head ?? {}) },
    };
  }

  /**
   * Sequence of axis labels following ISO 5457 / US NCS conventions:
   * `A, B, C, D, E, F, G, H, J, K, L, M, N, P, Q, ...` (I and O skipped).
   * For numeric mode returns `1, 2, 3, ...`.
   */
  static generateLabels(count: number, mode: GridLabelMode, skip: string[] = ["I", "O"]): string[] {
    if (mode === "numeric") {
      return Array.from({ length: count }, (_, i) => String(i + 1));
    }
    const labels: string[] = [];
    const skipSet = new Set(skip.map((s) => s.toUpperCase()));
    let i = 0;
    while (labels.length < count) {
      let n = i;
      let s = "";
      do {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      if (!skipSet.has(s)) labels.push(s);
      i++;
    }
    return labels;
  }

  private static buildParallelAxes(
    spacings: number[],
    labelMode: GridLabelMode,
    orientation: "horizontal" | "vertical",
    origin: [number, number],
  ): GridAxis[] {
    if (spacings.length === 0) return [];
    const positions: number[] = [0];
    for (let i = 0; i < spacings.length; i++) {
      positions.push(positions[i] + spacings[i]);
    }
    const total = positions[positions.length - 1];
    const halfPerp = total / 2 + 1;
    const labels = Grid.generateLabels(positions.length, labelMode);

    return positions.map((p, i) => {
      const startPerp = origin[orientation === "vertical" ? 1 : 0] - halfPerp;
      const endPerp = origin[orientation === "vertical" ? 1 : 0] + halfPerp;
      const alongOrigin = origin[orientation === "vertical" ? 0 : 1];
      const start: [number, number] =
        orientation === "vertical"
          ? [alongOrigin + p, startPerp]
          : [startPerp, alongOrigin + p];
      const end: [number, number] =
        orientation === "vertical"
          ? [alongOrigin + p, endPerp]
          : [endPerp, alongOrigin + p];
      return {
        tag: labels[i],
        curveSpec: { curve: "line", start, end },
        head: { start: true, end: true },
      };
    });
  }

  move(delta: Vec3): void {
    for (const axis of [...this.propertySet.uAxes, ...this.propertySet.vAxes]) {
      if (axis.curveSpec.curve === "line") {
        axis.curveSpec.start = [
          axis.curveSpec.start[0] + delta[0],
          axis.curveSpec.start[1] + delta[2],
        ];
        axis.curveSpec.end = [
          axis.curveSpec.end[0] + delta[0],
          axis.curveSpec.end[1] + delta[2],
        ];
      } else {
        axis.curveSpec.center = [
          axis.curveSpec.center[0] + delta[0],
          axis.curveSpec.center[1] + delta[2],
        ];
      }
    }
    this.propertySet.elevation += delta[1];
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(_angleDeg: number, _axis?: Vec3, _anchor?: Vec3): void {
    console.warn(
      `Grid.rotate not implemented (datumType=${this.propertySet.datumType}). Rotate axis curves directly via setOPConfig.`,
    );
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<GridOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
      head: { ...this.propertySet.head, ...(config.head ?? {}) },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): GridOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    const elevation = this.propertySet.elevation;
    const color = this.propertySet.color ?? DATUM_COLORS.GRID;
    const width = this.propertySet.lineWidth ?? 1;

    for (const axis of [...this.propertySet.uAxes, ...this.propertySet.vAxes]) {
      const axisGroup = this.buildAxis(axis, elevation, color, width);
      this.add(axisGroup);
      this.subElements3D.set(`axis-${axis.tag}`, axisGroup);
    }

    this.onDatumUpdated.trigger(null);
  }

  private buildAxis(axis: GridAxis, elevation: number, color: number, width: number) {
    const group = new THREE.Group();
    group.name = `grid-axis-${axis.tag}`;

    let startPt: Vector3;
    let endPt: Vector3;

    if (axis.curveSpec.curve === "line") {
      startPt = new Vector3(axis.curveSpec.start[0], elevation, axis.curveSpec.start[1]);
      endPt = new Vector3(axis.curveSpec.end[0], elevation, axis.curveSpec.end[1]);

      const dxRaw = axis.curveSpec.end[0] - axis.curveSpec.start[0];
      const dzRaw = axis.curveSpec.end[1] - axis.curveSpec.start[1];
      const axisLen = Math.sqrt(dxRaw * dxRaw + dzRaw * dzRaw) || 1;
      const axDirX = dxRaw / axisLen;
      const axDirZ = dzRaw / axisLen;

      if (this.propertySet.dashedAxes) {
        const dashed = buildDashedSegments(startPt, endPt, color, 0.4, 0.15);
        group.add(dashed);
      } else {
        const line = new Line({ start: startPt, end: endPt, color, fatLines: true, width });
        group.add(line);
      }

      const head = axis.head ?? { start: true, end: true };
      if (head.start) {
        const bubble = this.buildBubble(startPt, axis.tag, color, [-axDirX, -axDirZ]);
        group.add(bubble);
      }
      if (head.end) {
        const bubble = this.buildBubble(endPt, axis.tag, color, [axDirX, axDirZ]);
        group.add(bubble);
      }
    } else {
      const spec = axis.curveSpec;
      startPt = new Vector3(
        spec.center[0] + spec.radius * Math.cos(spec.startAngle),
        elevation,
        spec.center[1] + spec.radius * Math.sin(spec.startAngle),
      );
      endPt = new Vector3(
        spec.center[0] + spec.radius * Math.cos(spec.endAngle),
        elevation,
        spec.center[1] + spec.radius * Math.sin(spec.endAngle),
      );
      const arc = buildCircleLine(
        { x: spec.center[0], y: elevation, z: spec.center[1] },
        spec.radius,
        color,
        48,
        spec.startAngle,
        spec.endAngle,
      );
      group.add(arc);

      const head = axis.head ?? { start: true, end: true };
      if (head.start) {
        const outDir: [number, number] = [Math.cos(spec.startAngle), Math.sin(spec.startAngle)];
        const bubble = this.buildBubble(startPt, axis.tag, color, outDir);
        group.add(bubble);
      }
      if (head.end) {
        const outDir: [number, number] = [Math.cos(spec.endAngle), Math.sin(spec.endAngle)];
        const bubble = this.buildBubble(endPt, axis.tag, color, outDir);
        group.add(bubble);
      }
    }

    return group;
  }

  private buildBubble(
    anchor: Vector3,
    tag: string,
    color: number,
    outDir: [number, number],
  ): THREE.Group {
    const group = new THREE.Group();
    const radius = this.propertySet.head.diameter / 2;
    const offset = radius + 0.2;
    const bubbleCX = anchor.x + outDir[0] * offset;
    const bubbleCZ = anchor.z + outDir[1] * offset;
    const bubbleCenter = new Vector3(bubbleCX, anchor.y, bubbleCZ);

    const circle = buildCircleLine(bubbleCenter, radius, color);
    group.add(circle);

    const fontSize = this.propertySet.head.fontSize;
    const glyph = Glyphs.addGlyph(tag, fontSize, color, true);
    glyph.textMesh.geometry.computeBoundingBox();
    const bbox = glyph.textMesh.geometry.boundingBox!;
    const halfWidth = (bbox.max.x - bbox.min.x) * 0.5;
    const halfDepth = (bbox.max.y - bbox.min.y) * 0.5;
    // Default GlyphNode rotation (rotation.x = -π/2) lays text flat on XZ plane.
    // Local X → world X, local Y → world Z. Center by shifting -halfWidth in X, -halfDepth in Z.
    glyph.position.set(bubbleCX - halfWidth, anchor.y, bubbleCZ - halfDepth);
    group.add(glyph);

    return group;
  }

  getAxesSnapshot(): { u: GridAxis[]; v: GridAxis[] } {
    return {
      u: this.propertySet.uAxes.map((a) => ({ ...a })),
      v: this.propertySet.vAxes.map((a) => ({ ...a })),
    };
  }
}
